import { apiFetch } from '../config/api'

export interface AskEvansQuota {
  used: number
  limit: number
  percent: number
  tier: string
  resetHint: string
  warning?: boolean
}

export interface AskEvansSession {
  id: string
  title: string
  lastActiveAt: string
  createdAt: string
  messageCount: number
}

export interface AskEvansMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  query_log_id?: string
  user_rating?: number | null
  citations?: Array<{
    id: string
    kind?: string
    label?: string
    href?: string
    download?: {
      projectId: string
      pipeSetId: string
      reportId: string
      fileName: string
      kind: 'pqr' | 'wps'
      pqrNumber?: string
      importDraft?: boolean
      extractionId?: string
    }
  }>
  sources?: string[]
}

export interface AskEvansJob {
  job_id: string
  session_id: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  progress: string | null
  answer: string | null
  sources: string[] | null
  citations: Array<{
    id: string
    kind: string
    label?: string
    href?: string
    download?: {
      projectId: string
      pipeSetId: string
      reportId: string
      fileName: string
      kind: 'pqr' | 'wps'
      pqrNumber?: string
      importDraft?: boolean
      extractionId?: string
    }
  }> | null
  assistant_message_id?: string | null
  error: string | null
  quota: AskEvansQuota
}

export interface AskEvansSettings {
  enabled: boolean
  modelId: string
  region: string
  limits: { standard: string; plus: string; max: string }
  allowedEmails: string[]
}

export function getFeature() {
  return apiFetch<{ enabled: boolean; allowed: boolean }>('/api/ask-evans/feature')
}

export function listSessions() {
  return apiFetch<{ sessions: AskEvansSession[]; quota: AskEvansQuota }>('/api/ask-evans/sessions')
}

export function getSession(sessionId: string) {
  return apiFetch<{ session_id: string; title: string; messages: AskEvansMessage[] }>(
    `/api/ask-evans/sessions/${sessionId}`
  )
}

export function deleteSession(sessionId: string) {
  return apiFetch<{ deleted: boolean }>(`/api/ask-evans/sessions/${sessionId}`, { method: 'DELETE' })
}

export function sendChat(message: string, sessionId?: string | null) {
  return apiFetch<{ job_id: string; session_id: string }>('/api/ask-evans/chat', {
    method: 'POST',
    body: JSON.stringify({ message, sessionId: sessionId || undefined })
  })
}

export function getJob(jobId: string) {
  return apiFetch<AskEvansJob>(`/api/ask-evans/jobs/${jobId}`)
}

export function sendFeedback(sessionId: string, messageId: string, rating: 1 | -1) {
  return apiFetch(`/api/ask-evans/sessions/${sessionId}/messages/${messageId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ rating })
  })
}

export function getQuota() {
  return apiFetch<AskEvansQuota>('/api/ask-evans/quota')
}

export interface DirectoryUser {
  email: string
  name: string
}

export function listDirectoryUsers() {
  return apiFetch<{ users: DirectoryUser[] }>('/api/ask-evans/admin/directory')
}

export function getAdminSettings() {
  return apiFetch<AskEvansSettings>('/api/ask-evans/admin/settings')
}

export function saveAdminSettings(body: Partial<AskEvansSettings> & { enabled?: boolean }) {
  return apiFetch<AskEvansSettings>('/api/ask-evans/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(body)
  })
}

export interface UsageUser {
  userSub: string
  email: string | null
  displayName?: string | null
  tier: string
  used: number
  limit: number
  percent: number
  lastUsedAt?: string | null
}

export interface UsageReport {
  periodStart: string
  users: UsageUser[]
  jobs: Array<{
    id: string
    userSub: string
    status: string
    question: string
    roundsUsed: number
    toolsCalled: number
    createdAt?: string
  }>
}

export function getUsage(period?: string) {
  const query = period ? `?period=${encodeURIComponent(period)}` : ''
  return apiFetch<UsageReport>(`/api/ask-evans/admin/usage${query}`)
}

export function setUserTier(userSub: string, tier: string) {
  return apiFetch(`/api/ask-evans/admin/users/${encodeURIComponent(userSub)}/tier`, {
    method: 'PUT',
    body: JSON.stringify({ tier })
  })
}

export function grantTopup(userSub: string, tokens: number, expiresAt: string) {
  return apiFetch(`/api/ask-evans/admin/users/${encodeURIComponent(userSub)}/topups`, {
    method: 'POST',
    body: JSON.stringify({ tokens, expiresAt })
  })
}

export const REASONING_MODELS = [
  {
    id: 'us.anthropic.claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6 — us.anthropic.claude-sonnet-4-6'
  }
]

export const SUGGESTIONS = [
  'What PQR numbers are on a project?',
  'What was the lowest CTOD result?',
  'List pipe size sets and wall thickness',
  'What does PQR mean?'
]
